import { PronunciationPosition } from "../../settings/model/PronunciationPosition";
import { QuestionType } from "../../settings/model/QuestionType";
import Term, { getGroupString } from "../Term";
import styles from "../css//TermCell.module.css";
import TermLevelButton from "./TermLevelButton";
import SpeakButton from "../../common/SpeakButton";
import React, { useState } from "react";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import { getVoiceFile, makeBase64String } from "../../voice/util/voice";
import AudioPlayer from "react-h5-audio-player";
import { isHiraganaOnly } from "../../common/strings";
import AISentenceCard from "../../sentence/screen/AISentenceCard";
import SentenceGenerateButton from "../../sentence/view/SentenceGenerateButton";
import { SentenceGenerateButtonStatus } from "../../sentence/model/SentenceGenerateButtonStatus";
import MoreButton from "../../common/MoreButton";
import { ReactComponent as CheckBoxOnIcon } from "../../../img/iconImgAllCheckBoxOn.svg";
import { ReactComponent as CheckBoxOffIcon } from "../../../img/iconImgAllCheckBoxOff.svg";
import MarkButton from "../../common/MarkButton";
import classNames from "classnames";
import {
  TermSentenceWithStringValue,
  convertExampleSentenceStringValue,
} from "../../sentence/model/Sentence";

interface TermCellProps {
  term: Term;
  handleOnClick: (term: Term, isSelectionMode: boolean) => void;
  handleOnClickMarkButton: (term: Term) => void;
  handleOnClickLevelButton: (term: Term) => void;
  handleOnClickMoreButton: (
    event: React.MouseEvent<HTMLDivElement>,
    term: Term
  ) => void;
  handleOnClickGenerateSentenceButton: (term: Term) => void;
  handleOnClickSentenceLanguageButton: (term: Term) => void;
  handleOnClickMoreSentenceButton: (term: Term) => void;
  handleOnClickSentenceMoreButton: (
    event: React.MouseEvent<HTMLDivElement>,
    item: TermSentenceWithStringValue
  ) => void;
  config: {
    questionType: QuestionType;
    pronunciationPosition: PronunciationPosition;
    isSelectionMode: boolean;
    isShowDetailMode?: boolean;
    hideMoreButton?: boolean;
  };
}

export default function TermCell(props: TermCellProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string>();

  const firstText =
    props.config.questionType === QuestionType.termFirst ||
    props.config.questionType === QuestionType.showAll
      ? props.term.term
      : props.term.definition;
  const secondText =
    props.config.questionType === QuestionType.termFirst ||
    props.config.questionType === QuestionType.showAll
      ? props.term.definition
      : props.term.term;

  const cardContainerClass = classNames(styles.cardContainer, {
    [styles.cardDefaultContainer]: !props.term.selected,
    [styles.cardSelectedContainer]: props.term.selected,
  });

  const shouldShowMemo = () => {
    if (
      props.term.isShowDetail ||
      props.config.isShowDetailMode === true ||
      props.config.questionType === QuestionType.showAll ||
      props.config.isSelectionMode === true
    ) {
      return props.term.memo.length !== 0;
    }
  };

  const shouldShowPronunciation = () => {
    if (
      props.term.isShowDetail ||
      props.config.isShowDetailMode === true ||
      props.config.questionType === QuestionType.showAll ||
      props.config.isSelectionMode === true
    ) {
      return props.term.pronunciation.length !== 0;
    }
    if (
      props.config.questionType === QuestionType.termFirst &&
      props.config.pronunciationPosition === PronunciationPosition.onTerm
    ) {
      return props.term.pronunciation.length !== 0;
    }
    if (
      props.config.questionType === QuestionType.termFirst &&
      props.config.pronunciationPosition === PronunciationPosition.onDefinition
    ) {
      return false;
    }
    if (
      props.config.questionType === QuestionType.definitionFirst &&
      props.config.pronunciationPosition === PronunciationPosition.onTerm
    ) {
      return false;
    }
    if (
      props.config.questionType === QuestionType.definitionFirst &&
      props.config.pronunciationPosition === PronunciationPosition.onDefinition
    ) {
      return props.term.pronunciation.length !== 0;
    }
    return false;
  };

  function handleOnClickCell(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.handleOnClick(props.term, props.config.isSelectionMode);
  }

  function handleOnClickMarkButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.handleOnClickMarkButton(props.term);
  }

  function handleOnClickLevelButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.handleOnClickLevelButton(props.term);
  }

  function handleOnClickMoreButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.handleOnClickMoreButton(e, props.term);
  }

  function handleOnClickGenerateSentenceButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
    props.handleOnClickGenerateSentenceButton(props.term);
  }

  function handleOnClickSentenceLanguageButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
    props.handleOnClickSentenceLanguageButton(props.term);
  }

  function handleOnClickMoreSentenceButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
    props.handleOnClickMoreSentenceButton(props.term);
  }

  function handleOnClickSentenceMoreButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
    if (props.term.sentence === undefined) return;
    const item = new TermSentenceWithStringValue(
      props.term,
      props.term.sentence,
      convertExampleSentenceStringValue(props.term.sentence)
    );
    props.handleOnClickSentenceMoreButton(e, item);
  }

  function handleOnClickSpeakButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    speak();
  }

  async function speak() {
    let text = isHiraganaOnly(props.term.pronunciation)
      ? props.term.pronunciation
      : props.term.term;
    const groupToSpeak = props.term.group;

    setIsPlaying(true);
    try {
      const languageCode =
        groupToSpeak.length !== 0
          ? await fetchGroupLanguageCode(groupToSpeak)
          : "";
      const audio = await getVoiceFile(text, languageCode);
      setAudioSrc(makeBase64String(audio));
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  return (
    <>
      <div className={styles.container}>
        {props.config.isSelectionMode && (
          <div className={styles.selectionContainer}>
            {props.term.selected ? (
              <CheckBoxOnIcon className={styles.checkBox} />
            ) : (
              <CheckBoxOffIcon className={styles.checkBox} />
            )}
          </div>
        )}
        <div className={cardContainerClass}>
          <div className={styles.termCardContainer} onClick={handleOnClickCell}>
            <div className={styles.topContainer}>
              <div className={styles.termContainer}>
                <p className={styles.termText}>{firstText}</p>
                {props.term.isShowDetail === true ||
                props.config.isShowDetailMode === true ||
                props.config.questionType === QuestionType.showAll ||
                props.config.isSelectionMode === true ? (
                  <p className={styles.definitionText}>{secondText}</p>
                ) : null}
                {shouldShowMemo() && (
                  <p className={styles.memoText}>{props.term.memo}</p>
                )}
                {shouldShowPronunciation() && (
                  <p className={styles.pronunciationText}>
                    {props.term.pronunciation}
                  </p>
                )}
              </div>
              <div className={styles.topRightContainer}>
                <TermLevelButton
                  currentLevel={props.term.level}
                  handleOnClick={handleOnClickLevelButton}
                />
                {props.config.hideMoreButton !== true && (
                  <MoreButton onClick={handleOnClickMoreButton} />
                )}
              </div>
            </div>

            <div className={styles.bottomContainer}>
              <p className={styles.groupText}>
                {getGroupString(props.term.group)}
              </p>

              {!props.config.isSelectionMode && (
                <div className={styles.bottomRightContainer}>
                  <MarkButton
                    isMarked={props.term.isMarked}
                    onClick={handleOnClickMarkButton}
                  />

                  <SentenceGenerateButton
                    status={
                      props.term.isGeneratingSentence
                        ? SentenceGenerateButtonStatus.onLoading
                        : props.term.sentence === undefined
                          ? SentenceGenerateButtonStatus.inactive
                          : SentenceGenerateButtonStatus.active
                    }
                    onClick={handleOnClickGenerateSentenceButton}
                  />

                  <SpeakButton
                    fontSize="small"
                    isPlaying={isPlaying}
                    onClick={handleOnClickSpeakButton}
                  />
                </div>
              )}
            </div>
          </div>

          <AISentenceCard
            sentence={props.term.sentence}
            isMoreSentenceLoading={props.term.isMoreSentenceLoading}
            onClickLanguageButton={handleOnClickSentenceLanguageButton}
            onClickMoreSentenceButton={handleOnClickMoreSentenceButton}
            onClickMoreButton={handleOnClickSentenceMoreButton}
          />
        </div>
        {audioSrc && (
          <AudioPlayer
            autoPlay={true}
            src={audioSrc}
            style={{ display: "none" }}
            onEnded={() => {
              setIsPlaying(false);
              setAudioSrc(undefined);
            }}
          />
        )}
      </div>
    </>
  );
}
